import { createAction } from 'redux-starter-kit';
import { get, post } from '../../../../utility/via-fetch';
import { requestStart, requestFailed, requestSucceded } from '../../../domain/requests/RequestActions';
import { addMessage } from '../../application/messageUiActions';
import { putInspectionReason, putInspectionReasons } from '../../../domain/inspectionReasons/inspectionReasonActions';
import { putChecklists } from '../../../domain/checklists/checklistsActions';
import { putChecklistsConfig } from '../../../domain/checklistsConfig/checklistsConfigActions';

/**
 * An action that signifies a inspection reason is being currently edited.
 */
export const editInspectionReason = createAction('EDIT_INSPECTION_REASON');

/**
 * An action that signifies no inspection reason is being edited.
 */
export const cancelEditInspectionReason = createAction('CANCEL_EDIT_INSPECTION_REASON');

/**
 * An action that signifies a request to load inspection reasons has been made.
 */
export const fetchInspectionReasonPageData = createAction('FETCH_INSPECTION_REASONS_PAGE_DATA');

/**
 * An action for loading all the data for the inspection reasons page.
 * @example dispatch(requestInspectionReasonPageData())
 */
export function requestInspectionReasonPageData() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchInspectionReasonPageData.toString()));
    dispatch(fetchInspectionReasonPageData(requestId));

    try {
      const [reasons, checklists, checklistsConfig] = await Promise.all([get('/api/inspection-reasons/'), get('/api/checklists/'), get('/api/configuration/checklists/')]);

      // Special checklists value
      if (reasons != null && reasons.length > 0) {
        for (const inspectReason of reasons) {
          const beforeCode = (!inspectReason.hasOwnProperty('beforeDamageCheckListCode') || inspectReason.beforeDamageCheckListCode == null) ? '' : inspectReason.beforeDamageCheckListCode;
          const afterCode = (!inspectReason.hasOwnProperty('afterDamageCheckListCode') || inspectReason.afterDamageCheckListCode == null) ? '' : inspectReason.afterDamageCheckListCode;
          inspectReason.checkListCodesCombined = [beforeCode, afterCode];
        }
      }

      // Normalise data. Store the data in the domain, and the ids in the request response.
      dispatch(putInspectionReasons(reasons));
      dispatch(putChecklists(checklists));
      dispatch(putChecklistsConfig(checklistsConfig));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to add an inspection reason has been
 * made in the inspection reason configuration screen. This should pass through the request id.
 * @example dispatch(fetchAddInspectionReason(requestId))
 */
export const fetchAddInspectionReason = createAction('FETCH_CREATE_INSPECTION_REASON');

/**
 * An action for creating an inspection reason on the server.
 * @param {object} reason An inspection reason to be created.
 * @example dispatch(requestAddInspectionReason(reason));
 */
export function requestAddInspectionReason(reason) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAddInspectionReason.toString()));
    dispatch(fetchAddInspectionReason(requestId));
    try {
      reason.reasonCode = await post('/api/inspection-reasons', {}, reason); // eslint-disable-line no-param-reassign
      reason.checkListCodesCombined = [reason.beforeDamageCheckListCode, reason.afterDamageCheckListCode];
      dispatch(putInspectionReason(reason));
      dispatch(cancelEditInspectionReason());
      dispatch(requestSucceded(requestId, reason.reasonCode));
      dispatch(addMessage('success', 'itemSaved', { item: reason.reasonDescription }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to update an inspection reason
 * has been made in the inspection reason configuration screen.
 * This should pass through the request id.
 * @example dispatch(fetchUpdateInspectionReason(requestId));
 */
export const fetchUpdateInspectionReason = createAction('FETCH_UPDATE_INSPECTION_REASON');

export function requestUpdateInspectionReason(reason) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart());
    dispatch(fetchUpdateInspectionReason(requestId));
    try {
      await post(`/api/inspection-reasons/${reason.reasonCode}`, {}, reason);
      reason.checkListCodesCombined = [reason.beforeDamageCheckListCode, reason.afterDamageCheckListCode];
      dispatch(putInspectionReason(reason));
      dispatch(cancelEditInspectionReason());
      dispatch(requestSucceded(requestId, reason.reasonCode));
      dispatch(addMessage('success', 'itemUpdated', { item: reason.reasonDescription }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Publish (active) actions.
 */
export const addPublishInspectionReason = createAction('ADD_PENDING_PUBLISH_INSPECTION_REASON');
export const removePublishInspectionReason = createAction('REMOVE_PENDING_PUBLISH_INSPECTION_REASON');

export function requestPublishInspectionReason(reason) {
  const toggledReason = { ...reason, active: !reason.active };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(addPublishInspectionReason.toString()));
    dispatch(addPublishInspectionReason(toggledReason.reasonCode));
    try {
      await post(`/api/inspection-reasons/${reason.reasonCode}`, {}, toggledReason);
      toggledReason.checkListCodesCombined = [reason.beforeDamageCheckListCode, reason.afterDamageCheckListCode];
      dispatch(putInspectionReason(toggledReason));
      dispatch(addMessage('success', 'itemStateUpdated', { item: reason.reasonDescription }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
    dispatch(removePublishInspectionReason(toggledReason.reasonCode));
  };
}
